import React, { useRef } from 'react';
import IForm from '../../../types/IForm';
import { IFileItem } from '../../../types/api/FileItem';
import { DraggableAttachedFileListItem } from './partials/DraggableAttachedFileBaseListItem';
import { useWebsocketSyncedAttachments } from './useWebsocketSyncedAttachments';
import extendClassName from '../../../lib/bemUtils/bem';
import ContentMessage from '../ContentMessage/ContentMessage';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import FormHelp from '../../../components/molecules/Form/components/FormHelp/FormHelp';
import { useSortableList } from '../SortableContentList/useSortableList';
import { ContentList } from '../ContentList';
import { SortableListContextProvider } from '../../DragAndDrop';

export type AttachedFilesBaseSortableList = {
  attachedFileUpdateOrder: IForm | undefined;
  headline: string;
  headlineModifier?: Modifier<'border'>;
  hideNoAttachmentsMessage?: boolean;
  backgroundTransparent?: boolean;
  attachments?: IFileItem[];
  postId?: string;
};

export function AttachedFilesBaseSortableList({
  attachedFileUpdateOrder,
  attachments,
  backgroundTransparent,
  headline,
  headlineModifier,
  hideNoAttachmentsMessage = false,
  postId,
}: AttachedFilesBaseSortableList) {
  const { syncedAttachments, setSyncedAttachments } = useWebsocketSyncedAttachments(attachments, postId);
  const headerRef = useRef<HTMLHeadingElement | null>(null);
  const { t } = useSonTranslation('AttachedFiles');
  const { sortableItems, ...context } = useSortableList(
    [syncedAttachments, setSyncedAttachments],
    attachedFileUpdateOrder,
  );

  if (syncedAttachments.length === 0) {
    return hideNoAttachmentsMessage ? null : <ContentMessage text={t('noAttachments')} />;
  }

  return (
    <>
      <div className={extendClassName('attached-files-base__headline-container', headlineModifier)}>
        <h4 ref={headerRef} className="attached-files-base__headline">
          {headline}
        </h4>
        <FormHelp id="attached-files-base-sortable-list-form-help" helpText={t('helptext')} />
      </div>
      <SortableListContextProvider {...context}>
        <ContentList modifier={backgroundTransparent ? 'background-transparent' : null}>
          {sortableItems.map((attachment: IFileItem) => (
            <DraggableAttachedFileListItem
              // new list length => force rerender to ensure context menu entries are updated
              // -> submit sent old sorted list
              // SIBZIEL-13171, SIBZIEL-13166
              key={attachment.id + syncedAttachments.length}
              {...attachment}
              headerRef={headerRef}
            />
          ))}
        </ContentList>
      </SortableListContextProvider>
    </>
  );
}
